import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

const StackedBarChart = ({ data }) => {
    const chartRef = useRef(null);

    // Remove invalid entries
    data = data.filter(item => item.channels && item.channels.trim() !== '');

    useEffect(() => {
        if (!data || data.length === 0) return;

        const chart = echarts.init(chartRef.current);
        const isMobile = window.innerWidth <= 568;

        const channelsToGroup = [
            "Display", "Facebook", "Media", "Others", "Promotional", "Referral", "Cpc",
            "Safefra", "Shopzilla_cse", "Unspecified", "Youtube_Video", "Social_Other", "Affiliate"
        ];

        let transformedData = [...data];

        // Group into "Other" if mobile
        if (isMobile) {
            const grouped = {};
            transformedData.forEach(item => {
                const channelKey = channelsToGroup.includes(item.channels) ? 'Other' : item.channels;

                if (!grouped[channelKey]) {
                    grouped[channelKey] = {
                        channels: channelKey,
                        attribution_first_click_heuristic: 0,
                        attribution_last_click_heuristic: 0,
                        attribution_markov_algorithmic: 0,
                        attribution_shapley_size4_conv_rate_algorithmic: 0,
                    };
                }

                grouped[channelKey].attribution_first_click_heuristic += item.attribution_first_click_heuristic || 0;
                grouped[channelKey].attribution_last_click_heuristic += item.attribution_last_click_heuristic || 0;
                grouped[channelKey].attribution_markov_algorithmic += item.attribution_markov_algorithmic || 0;
                grouped[channelKey].attribution_shapley_size4_conv_rate_algorithmic += item.attribution_shapley_size4_conv_rate_algorithmic || 0;
            });

            transformedData = Object.values(grouped);
        }

        const channelList = transformedData.map(item => item.channels);

        // Prepare stacked values for each attribution type
        const stackedData = {
            'First Click': [],
            'Last Click': [],
            'Data Attribution 1': [],
            'Data Attribution 2': []
        };

        channelList.forEach(channel => {
            const item = transformedData.find(d => d.channels === channel);
            if (item) {
                stackedData['First Click'].push({
                    name: channel,
                    value: Math.round(item.attribution_first_click_heuristic)
                });
                stackedData['Last Click'].push({
                    name: channel,
                    value: Math.round(item.attribution_last_click_heuristic)
                });
                stackedData['Data Attribution 1'].push({
                    name: channel,
                    value: Math.round(item.attribution_markov_algorithmic)
                });
                stackedData['Data Attribution 2'].push({
                    name: channel,
                    value: Math.round(item.attribution_shapley_size4_conv_rate_algorithmic)
                });
            }
        });

        const series = channelList.map((channel, i) => ({
            name: channel,
            type: 'bar',
            stack: 'total',
            emphasis: { focus: 'series' },
            data: Object.keys(stackedData).map(key =>
                stackedData[key][i] ? stackedData[key][i].value : 0
            )
        }));

        const axisColor = window.matchMedia('(prefers-color-scheme: dark)').matches ? '#eee' : '#666';


        const option = {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' }
            },
            legend: {
                type: 'scroll',
                top: 50,
                textStyle: {
                    color: axisColor
                },
            },
            grid: {
                left: isMobile ? 1 : '2%',
                right: '4%',
                bottom: '3%',
                containLabel: true
            },
            xAxis: {
                type: 'value',
                axisLabel: {
                    color: axisColor,
                    rotate: isMobile ? 90 : 0,
                    fontSize: isMobile ? 10 : 12,
                    formatter: (value) => {
                        if (value >= 1000000) {
                            return (value / 1000000).toFixed(1) + 'M';
                        } else if (value >= 1000) {
                            return (value / 1000).toFixed(1) + 'K';
                        }
                        return value;
                    }
                }
            },
            yAxis: {
                type: 'category',
                data: ['First Click', 'Last Click', 'Data Attribution 1', 'Data Attribution 2'],
                axisLabel: {
                    color: axisColor,
                    rotate: 30,
                    formatter: function (value) {
                        const maxLen = isMobile ? 12 : 25;
                        return value.length > maxLen ? value.slice(0, maxLen) + '…' : value;
                    },
                    fontSize: isMobile ? 8 : 12,
                },
            },
            color: [
                '#e6194B', '#3cb44b', '#ffe119', '#4363d8', '#f58231',
                '#911eb4', '#46f0f0', '#f032e6', '#bcf60c', '#fabebe',
                '#008080', '#e6beff', '#9A6324', '#fffac8', '#800000',
                '#aaffc3', '#808000', '#ffd8b1', '#000075', '#808080'
            ],
            series
        };

        chart.setOption(option);

        const resize = () => chart.resize();
        window.addEventListener('resize', resize);

        return () => {
            chart.dispose();
            window.removeEventListener('resize', resize);
        };
    }, [data]);

    return (
        <div>
            <h3>Horizontal Attribution Type Chart</h3>
            <div ref={chartRef} style={{ width: '100%', height: '600px' }} />
        </div>
    );
};

export default StackedBarChart;
